// Canonical questionnaire name for staff/patient UI.
// Forms `display_name_da` / `display_name_en` is the source of truth.
// `template_summary.name` is a send-time snapshot; historical rows stored the id.

export const QUESTIONNAIRE_UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
export const QUESTIONNAIRE_PROM_SLUG_RE = /^prom-[a-z0-9]+(?:-[a-z0-9]+)*$/i;

export type QuestionnaireTemplateName = {
  id: string;
  display_name_da: string;
  display_name_en: string;
};

const JUNK_NAMES = new Set(['undefined', 'null', '[object object]']);

export function looksLikeInternalTemplateId(value: string | null | undefined): boolean {
  const v = value?.trim() ?? '';
  if (!v) return true;
  const compact = v.replace(/\s+/g, '');
  if (QUESTIONNAIRE_UUID_RE.test(v) || QUESTIONNAIRE_UUID_RE.test(compact)) return true;
  if (QUESTIONNAIRE_PROM_SLUG_RE.test(v)) return true;
  return false;
}

export function isUnusableSnapshotName(
  snapshotName: string | null | undefined,
  templateId: string,
): boolean {
  const name = snapshotName?.trim() ?? '';
  if (!name) return true;
  if (JUNK_NAMES.has(name.toLowerCase())) return true;
  if (name === templateId) return true;
  if (looksLikeInternalTemplateId(name)) return true;
  return false;
}

export function pickTemplateDisplayName(
  template: QuestionnaireTemplateName | undefined,
  locale?: string | null,
): string | null {
  if (!template) return null;
  const preferEn = (locale ?? 'da-DK').toLowerCase().startsWith('en');
  const primary = preferEn ? template.display_name_en : template.display_name_da;
  const secondary = preferEn ? template.display_name_da : template.display_name_en;
  const name = primary?.trim() || secondary?.trim() || '';
  return name || null;
}

export function indexQuestionnaireTemplates(
  rows: Iterable<QuestionnaireTemplateName>,
): Map<string, QuestionnaireTemplateName> {
  const map = new Map<string, QuestionnaireTemplateName>();
  for (const row of rows) {
    if (row.id) map.set(row.id, row);
  }
  return map;
}

export function resolveQuestionnaireDisplayName(args: {
  templateId: string;
  snapshotName?: string | null;
  locale?: string | null;
  templatesById: Map<string, QuestionnaireTemplateName> | Record<string, QuestionnaireTemplateName>;
  fallback: string;
  onMissing?: (templateId: string) => void;
}): string {
  const { templateId, snapshotName, locale, fallback, onMissing } = args;
  const map =
    args.templatesById instanceof Map
      ? args.templatesById
      : new Map(Object.entries(args.templatesById));

  const lookupKey = templateId || snapshotName?.trim() || '';
  const template =
    (lookupKey ? map.get(lookupKey) : undefined) ??
    (snapshotName ? map.get(snapshotName.trim()) : undefined);
  const resolved = pickTemplateDisplayName(template, locale);
  if (resolved && !isUnusableSnapshotName(resolved, templateId)) {
    return resolved;
  }

  if (!isUnusableSnapshotName(snapshotName, templateId)) {
    return snapshotName!.trim();
  }

  if (lookupKey) onMissing?.(lookupKey);
  const safeFallback = fallback.trim();
  if (safeFallback && !looksLikeInternalTemplateId(safeFallback)) return safeFallback;
  return 'Questionnaire';
}

/** forms-list-templates only accepts da-DK | en-DK. UI language is `da` / `en`. */
export function toFormsLocale(locale?: string | null): 'da-DK' | 'en-DK' | undefined {
  if (!locale) return undefined;
  const lower = locale.toLowerCase();
  if (lower === 'da-dk' || lower.startsWith('da')) return 'da-DK';
  if (lower === 'en-dk' || lower.startsWith('en')) return 'en-DK';
  return undefined;
}

export function assignmentQuestionnaireName(
  assignment: {
    forms_template_id: string;
    template_summary?: { name?: string | null; locale?: string | null } | null;
  },
  templatesById: Map<string, QuestionnaireTemplateName>,
  locale: string,
  fallback: string,
): string {
  return resolveQuestionnaireDisplayName({
    templateId: assignment.forms_template_id,
    snapshotName: assignment.template_summary?.name,
    locale: assignment.template_summary?.locale ?? locale,
    templatesById,
    fallback,
  });
}
